import type { RawSessionEvent, SessionEventCategory, SessionEventInternal } from '@repo/types/sessions/session-types';

export interface AggregatedSessionEvent {
    displayName: string;
    eventCategory: string;
    count: number;
    icon: string;
}

export const EVENT_CATEGORY_ICONS: Record<SessionEventCategory, string> = {
    deaths: 'skull',
    general: 'flag',
    kills: 'swords',
    objective_success: 'verified',
    objective_fail: 'cancel',
    achievements: 'rewarded_ads',
    mod: 'joystick',
};

export function getEventCategoryIcon(category: string): string {
    if (category in EVENT_CATEGORY_ICONS) {
        return EVENT_CATEGORY_ICONS[category as SessionEventCategory];
    }

    return 'star';
}

export const CHEAT_EVENT_TYPES = new Set(['cheat_activated', 'cheat_deactivated', 'cheat_changed']);

export function isGameEvent(eventType: string): boolean {
    return !CHEAT_EVENT_TYPES.has(eventType);
}

export function hasEventPosition(x: number, y: number, z: number): boolean {
    return x !== 0 || y !== 0 || z !== 0;
}

interface AggregateableEvent {
    eventType: string;
    eventName: string;
    eventCategory: string;
}

function aggregateEvents(events: AggregateableEvent[]): AggregatedSessionEvent[] {
    const gameEventMap = new Map<string, { count: number; eventCategory: string }>();

    for (const event of events) {
        if (!event.eventType || !event.eventName || !isGameEvent(event.eventType)) {
            continue;
        }

        const existing = gameEventMap.get(event.eventName);

        if (existing) {
            existing.count++;
        } else {
            gameEventMap.set(event.eventName, {
                count: 1,
                eventCategory: event.eventCategory || 'general',
            });
        }
    }

    return Array.from(gameEventMap.entries()).map(([name, data]) => ({
        displayName: name,
        eventCategory: data.eventCategory,
        count: data.count,
        icon: getEventCategoryIcon(data.eventCategory),
    }));
}

export function aggregateGameEvents(rawEvents: RawSessionEvent[]): AggregatedSessionEvent[] {
    return aggregateEvents(
        rawEvents
            .filter(
                (e): e is RawSessionEvent & { event_type: string; event_name: string } =>
                    e.event_type != null && e.event_name != null,
            )
            .map(e => ({
                eventType: e.event_type,
                eventName: e.event_name,
                eventCategory: e.event_category || 'general',
            })),
    );
}

export function aggregateInternalGameEvents(events: SessionEventInternal[]): AggregatedSessionEvent[] {
    return aggregateEvents(events);
}
